import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

const CreateGroupModal = ({ onClose, friends, onCreateGroup }) => {
  const [groupName, setGroupName] = useState('');
  const [selectedFriends, setSelectedFriends] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const handleFriendToggle = (friendId) => {
    setSelectedFriends(prev => {
      const newSet = new Set(prev);
      if (newSet.has(friendId)) {
        newSet.delete(friendId);
      } else {
        newSet.add(friendId);
      }
      return newSet;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedFriends.size === 0) {
      toast.error('Please select at least one friend');
      return;
    }

    setLoading(true);
    await onCreateGroup({
      name: groupName.trim() || null,
      memberIds: Array.from(selectedFriends), // Send user IDs instead of account numbers
    });
    setLoading(false);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Create Group</ModalTitle>
          <ModalCloseButton onClick={onClose}>×</ModalCloseButton>
        </ModalHeader>
        
        <ModalForm onSubmit={handleSubmit}>
          <ModalContent>
            <FormGroup>
              <FormLabel>Group Name (Optional)</FormLabel>
              <FormInput
                type="text"
                placeholder="Enter group name..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                maxLength={100}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Select Friends ({selectedFriends.size} selected)</FormLabel>
              <FriendsList>
                {friends.length === 0 ? (
                  <EmptyFriends>No friends available</EmptyFriends>
                ) : (
                  friends.map((friend) => (
                    <FriendItem
                      key={friend.id}
                      selected={selectedFriends.has(friend.id)}
                      onClick={() => handleFriendToggle(friend.id)}
                    >
                      <ModalFriendName>{friend.nickname || 'Anonymous'}</ModalFriendName>
                      <FriendCheckbox
                        type="checkbox"
                        checked={selectedFriends.has(friend.id)}
                        readOnly
                      />
                    </FriendItem>
                  ))
                )}
              </FriendsList>
            </FormGroup>
          </ModalContent>

          <ModalActions>
            <ModalButton type="button" variant="secondary" onClick={onClose}>
              Cancel
            </ModalButton>
            <ModalButton
              type="submit"
              variant="primary"
              disabled={loading || selectedFriends.size === 0}
            >
              {loading ? 'Creating...' : 'Create Group'}
            </ModalButton>
          </ModalActions>
        </ModalForm>
      </ModalContainer>
    </ModalOverlay>
  );
};

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContainer = styled.div`
  background-color: var(--clr-bg-secondary, #2a2a2a);
  border-radius: 12px;
  width: 90vw;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--clr-border, #444);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--clr-border, #444);
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  color: var(--clr-text, #ffffff);
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-text-secondary, #aaa);
  cursor: pointer;
  font-size: 1.5rem;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: var(--clr-bg-hover, #3a3a3a);
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const FormGroup = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid var(--clr-border, #444);
`;

const FormLabel = styled.label`
  display: block;
  color: var(--clr-text, #ffffff);
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  background-color: var(--clr-bg-tertiary, #333);
  border: 1px solid var(--clr-border, #444);
  border-radius: 8px;
  color: var(--clr-text, #ffffff);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--clr-primary, #007bff);
  }

  &::placeholder {
    color: var(--clr-text-secondary, #aaa);
  }
`;

const FriendsList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--clr-border, #444);
  border-radius: 8px;
  background-color: var(--clr-bg-tertiary, #333);

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: var(--clr-bg-secondary, #2a2a2a);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--clr-border, #444);
    border-radius: 2px;
  }
`;

const FriendItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: ${props => props.selected ? 'var(--clr-primary-faded, rgba(0, 123, 255, 0.1))' : 'transparent'};

  &:hover {
    background-color: ${props => props.selected ? 'var(--clr-primary-faded, rgba(0, 123, 255, 0.2))' : 'var(--clr-bg-hover, #3a3a3a)'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--clr-border, #444);
  }
`;

const ModalFriendName = styled.span`
  color: var(--clr-text, #ffffff);
  font-size: 14px;
`;

const FriendCheckbox = styled.input`
  margin: 0;
  cursor: pointer;
`;

const EmptyFriends = styled.div`
  padding: 20px;
  text-align: center;
  color: var(--clr-text-secondary, #aaa);
  font-size: 14px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  background-color: var(--clr-bg-tertiary, #333);
  border-top: 1px solid var(--clr-border, #444);
  flex-shrink: 0;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background-color: var(--clr-primary, #007bff);
    color: white;

    &:hover:not(:disabled) {
      background-color: #0056b3;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  ` : `
    background-color: var(--clr-bg-secondary, #2a2a2a);
    color: var(--clr-text, #ffffff);
    border: 1px solid var(--clr-border, #444);

    &:hover {
      background-color: var(--clr-bg-hover, #3a3a3a);
    }
  `}
`;

export default CreateGroupModal;